import cn from 'classnames';
import { FieldTypes } from 'constants/field-types-enum';
import { textWithReferences } from 'helpers/form';
import addValidators from 'helpers/validators/apply-validators';
import { useLocale } from 'hooks/locale';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormMessage, ValidationAlert } from 'components/FormComponents';
import FadeInMotion from 'components/Motion/FadeInMotion';
import ShakeMotion from 'components/Motion/ShakeMotion';
import StatusPill from 'components/Pills/StatusPills';

const QuestionWrapper = ({
  previewMode = false,
  question,
  questionIndex,
  textParams = {},
  control,
  register,
  setValue,
  trigger,
  getValues,
  locale = 'en_GB',
  error,
  componentMap,
  checkValues,
  getComponentProps,
  keyboardHint,
  animationDisabled = false,
  requirementVariantStyle = 'pill',
  gap = 'large',
  titleSize = 'xx-large',
  fieldName,
  hideHeader = false,
  hideOptionalTag = false, // Hide the "Optional" tag (useful for support fields)
  isVisible = true,
  allValues,
  errors,
  rejectedData,
  sectionHeader, // { title, helpText }
  noMargin = false, // Suppress component-level margins for consistent spacing
}) => {
  const { t } = useTranslation();
  const { dateHyphenFormat } = useLocale(locale);

  const QuestionComponent = componentMap[question.type];
  const name = fieldName || `${question.id}`;

  const deferHelpUntilChecked =
    !question?.showHelpAlways &&
    question?.showHelpWhenChecked &&
    (question?.type === FieldTypes.Checkbox || question?.type === FieldTypes.Consent);

  const helpWatchValue = useWatch({
    control,
    name,
    disabled: !deferHelpUntilChecked,
  });

  const showDeferredHelp =
    deferHelpUntilChecked &&
    (helpWatchValue === true || helpWatchValue === 'true' || helpWatchValue === 1);

  const { controlledInput = true, ...componentProps } = getComponentProps(
    question,
    undefined,
    undefined,
    {},
    undefined,
    false,
    {},
    {},
    noMargin,
  );

  // Create comprehensive validation rules
  const validationRules = isVisible
    ? addValidators(
        question.type,
        question.min,
        question.max,
        question.required,
        question.validatorName,
        question.validationParams,
        getValues,
        name,
      )
    : {};

  const noHeaderComponents = ['checkbox', 'consent', 'download', 'toggle_switch'];

  // Render section header if provided via prop
  const renderSectionHeader = () => {
    if (!sectionHeader) return null;

    // If only helpText exists (no title), render just the help text without section styling
    if (!sectionHeader.title && sectionHeader.helpText) {
      return (
        <div className="u-margin-top u-margin-bottom--small">
          <FormMessage
            message={textWithReferences(sectionHeader.helpText, textParams, { previewMode })}
            type="Info"
            noMargin
            closeable={false}
          />
        </div>
      );
    }

    // If title exists, render as a section header with margins
    // Extra margin-top for visual separation from previous section
    // No margin-bottom - gap will handle spacing, but we use negative margin to reduce it
    return (
      <div className="d-flex flex-column u-margin-top--large u-margin-bottom--small">
        {sectionHeader.title && (
          <span className="primary-text u-font-size-x-large u-font-weight-600">
            {textWithReferences(sectionHeader.title, textParams, { previewMode })}
          </span>
        )}
        {sectionHeader.helpText && (
          <div className="u-margin-top--small">
            <FormMessage
              message={textWithReferences(sectionHeader.helpText, textParams, { previewMode })}
              type="Info"
              noMargin
              closeable={false}
            />
          </div>
        )}
      </div>
    );
  };

  if (question?.type === 'statement') {
    return (
      <>
        {renderSectionHeader()}
        <div className="d-flex flex-column u-gap-small">
          {question?.title != null && question?.title !== '' && (
            <div
              className="html-embedded-lists primary-text u-font-size-large u-font-weight-500 white-space-pre-line"
              dangerouslySetInnerHTML={{
                __html: textWithReferences(question.title, textParams, { previewMode }) ?? '',
              }}
            />
          )}
          {question?.text != null && question.text !== '' && (
            <div
              className="html-embedded-lists secondary-text u-font-size-small u-font-weight-400 white-space-pre-line"
              dangerouslySetInnerHTML={{
                __html: textWithReferences(question.text, textParams, { previewMode }) ?? '',
              }}
            />
          )}
          {question?.helpText && (
            <FormMessage
              message={textWithReferences(question.helpText, textParams, { previewMode })}
              type="Info"
              noMargin
              closeable={false}
            />
          )}
        </div>
      </>
    );
  }

  if (question.type === 'object') {
    return null;
  }

  if (!QuestionComponent) {
    return <p>Unsupported question type: {question.type}</p>;
  }

  return (
    <>
      {renderSectionHeader()}
      <div
        className={` flex-column u-gap-${gap} u-width-100 ${question.type === 'hidden' ? 'd-hide' : 'd-flex'}`}>
        {!hideHeader && (
          <>
            {!hideOptionalTag && requirementVariantStyle === 'pill' && (
              <span className="secondary-text u-font-size-md u-font-weight-400 u-margin-bottom--small">
                <ShakeMotion shake={error} disabled={animationDisabled}>
                  <StatusPill
                    text={question.required ? 'Required' : 'Optional'}
                    large
                    status={question.required ? 'warning' : 'neutral'}
                    show={questionIndex + 1}
                  />
                </ShakeMotion>
              </span>
            )}
            {!noHeaderComponents.includes(question.type) && (
              <div className="d-flex flex-column u-gap-small">
                <div className="d-flex flex-row u-gap-small align-items-center">
                  <span
                    className={`primary-text u-font-size-${titleSize} u-font-weight-500`}
                    dangerouslySetInnerHTML={{
                      __html: textWithReferences(question?.text || question?.title, textParams, {
                        previewMode,
                      }),
                    }}></span>
                  {!hideOptionalTag && requirementVariantStyle === 'text' && !question.required && (
                    <StatusPill text={'Optional'} status={'light'} />
                  )}
                </div>
                {(question?.use_description ?? true) && question?.description && (
                  <span className="secondary-text u-font-size-x-small u-font-weight-400 white-space-pre-line">
                    <span
                      dangerouslySetInnerHTML={{
                        __html: textWithReferences(question.description, textParams, {
                          previewMode,
                        }),
                      }}
                    />
                  </span>
                )}
              </div>
            )}
          </>
        )}
        <div className="d-flex flex-column u-gap-small">
          {controlledInput ? (
            <Controller
              name={name}
              control={control}
              rules={validationRules}
              defaultValue={question?.defaultValue}
              render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => {
                const normalizedValue = (() => {
                  if (value !== undefined && value !== null) {
                    return value;
                  }
                  if (question?.defaultValue !== undefined && question?.defaultValue !== null) {
                    return question.defaultValue;
                  }
                  if (
                    question?.type === FieldTypes.Checkbox ||
                    question?.type === FieldTypes.Consent
                  ) {
                    return false;
                  }
                  if (
                    question?.type === FieldTypes.MultipleChoice &&
                    question?.options?.multiple_selection
                  ) {
                    return [];
                  }
                  if (question?.type === FieldTypes.MultiSelect) {
                    return [];
                  }
                  if (question?.type === FieldTypes.MultiUpload) {
                    return [];
                  }
                  return '';
                })();

                const valueForComponent =
                  question?.type === FieldTypes.MultiUpload && !Array.isArray(normalizedValue)
                    ? []
                    : normalizedValue;
                const componentPropsFromGetter = getComponentProps(valueForComponent);
                // Coerce checked to boolean for Checkbox/Consent/ToggleSwitch (form/API may supply string e.g. "true"/"false")
                const checkedProp =
                  question?.type === FieldTypes.Checkbox ||
                  question?.type === FieldTypes.Consent ||
                  question?.type === FieldTypes.ToggleSwitch
                    ? normalizedValue === true ||
                      normalizedValue === 'true' ||
                      normalizedValue === 1
                    : normalizedValue;
                return (
                  <QuestionComponent
                    {...componentPropsFromGetter}
                    question={question}
                    format={dateHyphenFormat}
                    value={
                      (question?.type === FieldTypes.MultiUpload ||
                        question?.type === FieldTypes.DocumentUpload) &&
                      componentPropsFromGetter.value != null
                        ? componentPropsFromGetter.value
                        : valueForComponent
                    }
                    textParams={textParams}
                    checked={checkedProp}
                    onChange={onChange}
                    error={fieldError?.message || error}
                    setValue={setValue}
                    trigger={trigger}
                    componentMap={componentMap}
                    showKeyboardHelp
                    // Pass react-hook-form props for IdRequirementsWrapper
                    control={control}
                    getValues={getValues}
                    name={name}
                    allValues={allValues}
                    errors={errors}
                    rejectedData={rejectedData}
                    noMargin={true}
                  />
                );
              }}
            />
          ) : (
            <QuestionComponent
              {...componentProps}
              question={question}
              componentMap={componentMap}
              format={dateHyphenFormat}
              register={register}
              control={control}
              setValue={setValue}
              trigger={trigger}
              name={name}
              validators={validationRules}
              showKeyboardHelp
              getComponentProps={getComponentProps}
              getValues={getValues}
              isVisible={isVisible}
              allValues={allValues}
              errors={errors}
              error={error}
              rejectedData={rejectedData}
              noMargin={noMargin}
            />
          )}
          {noHeaderComponents.includes(question.type) && (
            <div className="d-flex flex-column u-gap-small">
              <span className="secondary-text u-font-size-x-small u-font-weight-400 white-space-pre-line">
                <span
                  dangerouslySetInnerHTML={{
                    __html: textWithReferences(question.description, textParams, { previewMode }),
                  }}
                />
              </span>
            </div>
          )}
          {keyboardHint}
        </div>
        {question?.helpText && (!deferHelpUntilChecked || showDeferredHelp) && (
          <div className="u-margin-top--small">
            <FormMessage message={question?.helpText} type="Info" noMargin closeable={false} />
          </div>
        )}
        {error && (
          <div
            className={`u-margin-top--small u-margin-bottom--small ${question?.type === 'dynamic_array' ? 'u-order-1' : ''}`}>
            <FadeInMotion key={name} disabled={animationDisabled}>
              <ValidationAlert
                show={true}
                message={typeof error === 'string' ? error : error?.message}
              />
            </FadeInMotion>
          </div>
        )}
      </div>
    </>
  );
};

export default QuestionWrapper;
